import axios from 'axios';
import makeLogger from './logger';

const log = makeLogger('api');

// The BFF lives on a separate origin (separate Vercel project), so every request
// must send the cross-site session cookie.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true
});

// Log every BFF call: method, path, status, and round-trip time. A 401 here is the SPA-visible signal
// that the session expired (e.g. the access token lapsed and could not be refreshed).
api.interceptors.request.use((cfg) => {
  cfg.metadata = { start: Date.now() };
  log.debug(`→ ${(cfg.method || 'get').toUpperCase()} ${cfg.url}`, cfg.params || '');
  return cfg;
});
api.interceptors.response.use(
  (res) => {
    const ms = Date.now() - ((res.config.metadata && res.config.metadata.start) || Date.now());
    log.info(`← ${(res.config.method || 'get').toUpperCase()} ${res.config.url} ${res.status} (${ms}ms)`);
    return res;
  },
  (err) => {
    const cfg = err.config || {};
    const ms = Date.now() - ((cfg.metadata && cfg.metadata.start) || Date.now());
    const status = err.response ? err.response.status : 'ERR';
    const detail = (err.response && err.response.data && err.response.data.message) || err.message;
    log.warn(`← ${(cfg.method || 'get').toUpperCase()} ${cfg.url} ${status} (${ms}ms): ${detail}`);
    // A 401 means the BFF session is gone — token lapsed, or this user was logged out from another
    // product (back-channel logout purged the session). Send the SPA to /login. Guard against loops:
    // the /login page makes no protected calls, so it never re-triggers this.
    if (status === 401 && window.location.pathname !== '/login') {
      log.info('401 from BFF — session ended (possibly global logout); redirecting to /login');
      window.location.assign('/login');
    }
    return Promise.reject(err);
  }
);

// Kick off server-side login (top-level navigation, not XHR). Pass a milientOrgId to log directly into
// a specific org (Case 1 — the gateway targets that Auth0 org, no picker); omit it for the plain flow
// (Case 2 — Auth0 shows its org prompt).
export const goToLogin = (milientOrgId) => {
  const q = milientOrgId ? `?milient_org_id=${encodeURIComponent(milientOrgId)}` : '';
  window.location.href = `${BACKEND_URL}/api/login${q}`;
};

// PUBLIC (pre-auth) list of orgs a user can log directly into, for the login-screen picker.
export const getLoginOrganizations = () => api.get('/login-organizations');

// Switch the active organization (top-level navigation — it's an OIDC redirect flow through the
// gateway, not an XHR). The gateway translates `milientOrgId` -> Auth0 org and re-federates silently;
// no credential re-entry.
export const goToSwitchOrg = (milientOrgId) => {
  window.location.href = `${BACKEND_URL}/api/switch-org?milient_org_id=${encodeURIComponent(milientOrgId)}`;
};

export default api;
